import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  useMediaQuery,
  Stack,
  Fade,
  TextField,
  InputAdornment,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { decryptField } from "../../utils/encryption";
import {
  getFirestore,
  collection,
  getDocs,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { auth } from "../../firebase";
import ClientForm from "./ClientForm";

function getInitials(name) {
  if (!name) return "";
  const names = name.trim().split(" ");
  if (names.length === 1) return names[0][0];
  return names[0][0] + (names[1]?.[0] || "");
}

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [openForm, setOpenForm] = useState(false);
  const [snackbar, setSnackbar] = useState("");
  const [editingClient, setEditingClient] = useState(null);
  const [search, setSearch] = useState("");
  const db = getFirestore();
  const isMobile = useMediaQuery("(max-width:700px)");

  const fetchClients = async () => {
    const user = auth.currentUser;
    if (!user) return;
    setLoading(true);
    const snapshot = await getDocs(
      collection(db, "users", user.uid, "clients")
    );
    const result = snapshot.docs
      .map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          name: decryptField(data.name || ""),
          email: decryptField(data.email || ""),
          company: decryptField(data.company || ""),
          address: decryptField(data.address || ""),
          notes: decryptField(data.notes || ""),
        };
      })
      .filter((c) => c.name || c.email);
    setClients(result);
    setLoading(false);
  };

  useEffect(() => {
    fetchClients();
    // eslint-disable-next-line
  }, []);

  const handleDelete = async (clientId) => {
    const user = auth.currentUser;
    if (!user) return;
    await deleteDoc(doc(db, "users", user.uid, "clients", clientId));
    setClients((prev) => prev.filter((c) => c.id !== clientId));
    setSnackbar("Client deleted");
  };

  // Filter clients by search string (name, email, company, address, notes)
  const normalizedSearch = search.trim().toLowerCase();
  const filteredClients = !normalizedSearch
    ? clients
    : clients.filter((client) => {
        const values = [
          client.name,
          client.email,
          client.company,
          client.address,
          client.notes,
        ]
          .join(" ")
          .toLowerCase();
        return (
          values.includes(normalizedSearch) ||
          values.indexOf(normalizedSearch) !== -1 ||
          values.match(normalizedSearch)
        );
      });

  return (
    <Box
      p={isMobile ? 1 : 4}
      sx={{
        maxWidth: 950,
        margin: "0 auto",
        minHeight: "60vh",
        background: "#fff",
        borderRadius: { xs: 2, md: 4 },
        boxShadow: "0 4px 24px rgba(64, 85, 130, 0.08)",
        mt: isMobile ? 1 : 5,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2} mb={2}>
        <PeopleAltRoundedIcon color="primary" sx={{ fontSize: 36 }} />
        <Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Clients & Contacts
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            fontWeight={400}
            sx={{ maxWidth: 600 }}
          >
            Save your clients here to create invoices in seconds, auto-fill
            billing details, and keep your freelance business organized.
            <Box
              component="span"
              sx={{ color: "primary.main", fontWeight: 600, ml: 1 }}
            >
              No more messy spreadsheets.
            </Box>
          </Typography>
        </Box>
      </Stack>

      <Paper
        elevation={0}
        sx={{
          background: "#F8FAFC",
          border: "1px solid #E5E7EB",
          borderRadius: 4,
          p: isMobile ? 2 : 4,
          mb: 3,
        }}
      >
        <Stack
          direction={isMobile ? "column" : "row"}
          alignItems={isMobile ? "flex-start" : "center"}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Why add your clients?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                <li>Invoice instantly — auto-fill client details</li>
                <li>See full client history & notes</li>
                <li>
                  Track who paid, who owes (and never forget a client again)
                </li>
                <li>Export for tax, CRM, or accountant anytime</li>
              </ul>
            </Typography>
          </Box>
          <Stack
            direction={isMobile ? "column" : "row"}
            alignItems="center"
            spacing={isMobile ? 2 : 1}
            width={isMobile ? "100%" : "auto"}
            sx={{ mt: isMobile ? 2 : 0, minWidth: 220 }}
          >
            {clients.length > 0 && (
              <TextField
                placeholder="Search clients…"
                value={search}
                size="small"
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                  bgcolor: "#fff",
                  borderRadius: 2,
                  minWidth: isMobile ? "100%" : 170,
                  boxShadow: "0 1px 6px #0001",
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" sx={{ fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: search ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearch("")}
                        aria-label="Clear search"
                        tabIndex={-1}
                        sx={{ opacity: 0.8 }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                  "aria-label": "Search clients",
                }}
              />
            )}
            <Button
              variant="contained"
              color="primary"
              size={isMobile ? "large" : "medium"}
              startIcon={<PersonAddAltRoundedIcon />}
              onClick={() => setOpenForm(true)}
              sx={{
                minWidth: 160,
                borderRadius: 3,
                fontWeight: 600,
                boxShadow: "0 2px 8px 0 #38bdf81a",
                width: isMobile ? "100%" : "auto",
              }}
            >
              Add Client
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* MAIN CONTENT */}
      {loading ? (
        <Fade in>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={120}
          >
            <CircularProgress color="primary" />
          </Box>
        </Fade>
      ) : clients.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            textAlign: "center",
            py: 7,
            background: "linear-gradient(90deg,#f0fdfa 0%,#f3f4f6 100%)",
            border: "1px dashed #bae6fd",
            borderRadius: 4,
            mt: 2,
          }}
        >
          <Avatar
            sx={{
              bgcolor: "#38bdf8",
              width: 64,
              height: 64,
              mx: "auto",
              mb: 2,
              fontSize: 38,
              fontWeight: 700,
            }}
          >
            <AddIcon fontSize="large" />
          </Avatar>
          <Typography variant="h6" fontWeight={700}>
            Add your first client
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 1, mb: 3, maxWidth: 420, mx: "auto" }}
          >
            Easily save your clients and contacts so you can send invoices,
            track payments, and keep your business running smoothly.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<PersonAddAltRoundedIcon />}
            sx={{ borderRadius: 3, fontWeight: 600 }}
            onClick={() => setOpenForm(true)}
            aria-label="Add your first client"
          >
            Add Client
          </Button>
        </Paper>
      ) : (
        <Fade in>
          <Paper
            elevation={0}
            sx={{
              mt: 3,
              overflow: "auto",
              borderRadius: 3,
              border: "1px solid #e5e7eb",
              boxShadow: "0 2px 12px 0 #e0f2fe36",
              p: isMobile ? 0 : 2,
            }}
          >
            {filteredClients.length === 0 ? (
              <Box py={6} textAlign="center" color="text.secondary">
                <Typography variant="h6" fontWeight={700} mb={1}>
                  No clients found
                </Typography>
                <Typography variant="body2">
                  Try another name, email, company, or address.
                </Typography>
              </Box>
            ) : (
              <Table
                sx={{
                  minWidth: 650,
                  "& th": {
                    background: "#f1f5f9",
                    fontWeight: 600,
                    fontSize: "1rem",
                    letterSpacing: "0.03em",
                  },
                  "& td, & th": {
                    borderBottom: "1px solid #e5e7eb",
                  },
                }}
                size="small"
                aria-label="clients table"
              >
                <TableHead>
                  <TableRow>
                    <TableCell></TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredClients.map((client) => (
                    <TableRow
                      key={client.id}
                      hover
                      sx={{
                        "&:hover": { background: "#f0f9ff" },
                        transition: "background 0.2s",
                      }}
                    >
                      <TableCell>
                        <Avatar
                          sx={{
                            bgcolor: "#bae6fd",
                            color: "#0369a1",
                            fontWeight: 600,
                          }}
                        >
                          {getInitials(client.name)}
                        </Avatar>
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight={600}>{client.name}</Typography>
                        {client.notes && (
                          <Tooltip title={client.notes} arrow>
                            <InfoOutlinedIcon
                              sx={{
                                color: "#a3a3a3",
                                ml: 1,
                                fontSize: 18,
                                verticalAlign: "middle",
                              }}
                            />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{client.email}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {client.company}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {client.address?.length > 30
                            ? client.address.slice(0, 30) + "…"
                            : client.address}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit client" arrow>
                          <IconButton
                            onClick={() => setEditingClient(client)}
                            size="small"
                            sx={{
                              bgcolor: "#f0f9ff",
                              mr: 1,
                              "&:hover": { bgcolor: "#bae6fd" },
                            }}
                            aria-label={`Edit client ${client.name}`}
                          >
                            <EditOutlinedIcon color="primary" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete client" arrow>
                          <IconButton
                            onClick={() => setConfirmDeleteId(client.id)}
                            size="small"
                            sx={{
                              bgcolor: "#fef2f2",
                              "&:hover": { bgcolor: "#fecaca" },
                            }}
                            aria-label={`Delete client ${client.name}`}
                          >
                            <DeleteOutlineIcon sx={{ color: "#ef4444" }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        </Fade>
      )}

      {/* Client Form Modal */}
      <ClientForm
        open={openForm || !!editingClient}
        onClose={() => {
          setOpenForm(false);
          setEditingClient(null);
        }}
        initialData={editingClient}
        onSuccess={(msg) => {
          setSnackbar(msg);
          setOpenForm(false);
          setEditingClient(null);
          fetchClients();
        }}
      />

      {/* Snackbar Alert */}
      <Snackbar
        open={!!snackbar}
        autoHideDuration={3000}
        onClose={() => setSnackbar("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar("")}
          severity="success"
          variant="filled"
        >
          {snackbar}
        </Alert>
      </Snackbar>

      {/* Confirm Deletion Modal */}
      <Dialog open={!!confirmDeleteId} onClose={() => setConfirmDeleteId(null)}>
        <DialogTitle>Delete Client?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this client? Their info will be
            removed from your account but not from past invoices.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteId(null)} variant="outlined">
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={async () => {
              await handleDelete(confirmDeleteId);
              setConfirmDeleteId(null);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
